import React from "react";
import { default as Markdown } from "react-markdown";
import { connect } from "react-redux";

import initialState from "core/reducers/initialState";
import DictionaryUtil from "core/util/dictionary/DictionaryUtil";
import TokenSession from "core/util/session/TokenSession";
import { DictionaryEntryFormPopover } from "./DictionaryEntryForm";
import { LabelRegistry } from "./ILabel";
import { ILabelDefinition } from "./LabelProvider";
import LabelUtil from "./LabelUtil";

/**
 * @description
 * Connected component that finds the most relevant label available in the dictionary,
 * formats the string based on available arguments, and renders plain text/markdown based on selected
 * content type.
 *
 * @author chris
 */

export interface ILabelProps extends ILabelDefinition {
	args?: ILabelArgs;
	instances?: string[];
	markdownRenderers?: {
		[key: string]: React.ReactType<any>;
	};
	children?: (text: React.ReactNode) => React.ReactNode;
}

interface ILabelStateProps {
	data: {
		formatted: string,
		type: string
	};
	editingEnabled?: boolean;
}

type ResultProps = ILabelProps & ILabelStateProps;

interface ILabelArgs {
	[key: string]: Date | string | number | null | undefined;
}

function mapStateToProps({dictionary = {}, user, edit} = initialState, ownProps: ILabelProps): ILabelStateProps {

	// get TokenSession language
	const language: string = user.language;

	// find most relevant value
	const result = DictionaryUtil.resolveValue(dictionary, { value: ownProps.v, type: ownProps.type }, ownProps.k, language, ownProps.instances);

	// register meta for editor
	if (edit.enabled) {
		LabelRegistry.register({k: ownProps.k, v: ownProps.v});
	}

	// return the formatted stuff
	return {
		data: {
			formatted: LabelUtil.formatTemplate(result.value, ownProps.args),
			type: result.type
		},
		// admins are allowed to edit labels now that they're not stored in ContextSettings
		editingEnabled: edit.enabled || TokenSession.hasRoles("ROLE_ADMIN")
	};
}

const Label: React.SFC<ResultProps> = (props): any => {

	const label = (props.allowHtml && "HTML" === props.type) ? React.createElement(props.element || 'span', {dangerouslySetInnerHTML: {__html: props.data.formatted}}) :

		(props.allowMarkdown && "MARKDOWN" === props.type) ? <Markdown source={props.data.formatted} renderers={props.markdownRenderers} /> :

		props.data.formatted;

	const c = typeof props.children === 'function' ? props.children(label || null) : label || null;

	if (props.editingEnabled) {

		return (
			<DictionaryEntryFormPopover
				k={props.k}
				v={props.v}
				argNames={props.args && Object.keys(props.args)}
				instances={props.instances}
				type={props.type}
				allowMarkdown={props.allowMarkdown}
			>
				{c}
			</DictionaryEntryFormPopover>
		);
	}

	return c;
};

export default connect(mapStateToProps)(Label);




// WEBPACK FOOTER //
// ./src/core/components/label/Label.tsx